// Kern-Verträge (Besitzer: api-kern): Konten, Sitzungen, Tokens, Gerätefluss, Aufgaben,
// Benachrichtigungen, Ereignisse, Suche, Einstellungen, Paginierung (SPEC §1.2, §5.2, §5.4, §7, R-38).
import { z } from 'zod';

// ── Rollen ──────────────────────────────────────────────────────────────────
/** Rollen-Kürzel aus SPEC §1.2. LM, OM, HOD und OWN sind abgeleitet, der Rest kommt aus Zuweisungen. */
export const ROLE_KEYS = [
  'EMP',
  'LM',
  'OM',
  'HOD',
  'PERF',
  'COMP',
  'HR',
  'CL',
  'CEO',
  'ADM',
  'OWN',
] as const;
export const roleKeySchema = z.enum(ROLE_KEYS);
export type RoleKey = z.infer<typeof roleKeySchema>;

/** Rollen, die die Admin-Plattform öffnen dürfen (SPEC §1.3). */
export const ADMIN_CAPABLE_ROLES = [
  'ADM',
  'HR',
  'PERF',
  'COMP',
  'CL',
  'CEO',
] as const satisfies readonly RoleKey[];

/** Gespeicherte Rollen (`bar.role` in der DB) ↔ Kürzel. */
export const ASSIGNABLE_ROLES = {
  performance: 'PERF',
  compensation: 'COMP',
  hr: 'HR',
  c_level: 'CL',
  ceo: 'CEO',
  admin: 'ADM',
} as const satisfies Record<string, RoleKey>;
export type AssignableRole = keyof typeof ASSIGNABLE_ROLES;
export const assignableRoleSchema = z.enum(
  Object.keys(ASSIGNABLE_ROLES) as [AssignableRole, ...AssignableRole[]],
);

export const authModeSchema = z.enum(['supabase', 'local', 'demo']);
export const accountKindSchema = z.enum(['person', 'service', 'bot']);
export const accountStatusSchema = z.enum(['invited', 'active', 'suspended']);
export const themeSchema = z.enum(['dark', 'light', 'system']);
export const platformSchema = z.enum(['people', 'admin', 'ai']);

// ── Paginierung (SPEC §7) ───────────────────────────────────────────────────
export const pageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(500).optional(),
});
export type PageQuery = z.infer<typeof pageQuerySchema>;

export const pageOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    total: z.number().optional(),
  });
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  total?: number;
}

// ── Auth ────────────────────────────────────────────────────────────────────
/** `details[].message` eines 403, wenn die E-Mail-Domain nicht erlaubt ist (ALLOWED_EMAIL_DOMAINS). */
export const EMAIL_DOMAIN_NOT_ALLOWED = 'email_domain_not_allowed';

const email = z.string().trim().toLowerCase().pipe(z.email().max(320));
const password = z.string().min(1).max(512);

export const authConfigSchema = z.object({
  mode: authModeSchema,
  appName: z.string(),
  personasEnabled: z.boolean(),
  /** Heutiges Datum der Server-Uhr (APP_TODAY), für Fälligkeiten und „Today/Yesterday“ im Web. */
  today: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  supabase: z.object({ url: z.string(), anonKey: z.string() }).optional(),
  /** Nur diese E-Mail-Domains dürfen sich anmelden (ALLOWED_EMAIL_DOMAINS); fehlt = alle. */
  allowedEmailDomains: z.array(z.string()).optional(),
});
export type AuthConfig = z.infer<typeof authConfigSchema>;

export const loginSchema = z.object({ email, password });
export const personaLoginSchema = z.object({ key: z.string().regex(/^[a-z0-9._-]{1,64}$/i) });
export const switchAccountSchema = z.object({ accountId: z.uuid() });
export const logoutSchema = z.object({ all: z.boolean().optional() });
export const inviteSchema = z.object({
  email,
  displayName: z.string().trim().min(1).max(200).optional(),
  employeeId: z.uuid().optional(),
  roles: z.array(assignableRoleSchema).max(6).optional(),
});
export const inviteAcceptSchema = z.object({ token: z.string().min(10).max(200), password });
export const resetRequestSchema = z.object({ email });
export const resetConfirmSchema = z.object({ token: z.string().min(10).max(200), password });
export const passwordChangeSchema = z.object({ currentPassword: password, newPassword: password });

export const inviteInfoSchema = z.object({
  valid: z.boolean(),
  reason: z.enum(['expired', 'used', 'revoked', 'unknown']).optional(),
  email: z.string().optional(),
  displayName: z.string().optional(),
});
export type InviteInfo = z.infer<typeof inviteInfoSchema>;

// ── Me ──────────────────────────────────────────────────────────────────────
export const meSchema = z.object({
  account: z.object({
    id: z.uuid(),
    kind: accountKindSchema,
    email: z.string(),
    displayName: z.string(),
    status: accountStatusSchema,
    theme: themeSchema,
    lastLoginAt: z.string().nullable(),
    notificationPrefs: z.record(z.string(), z.unknown()),
  }),
  employee: z
    .object({
      id: z.uuid(),
      displayName: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      jobTitle: z.string(),
      teamId: z.uuid(),
      departmentId: z.uuid(),
      managerId: z.uuid().nullable(),
      avatarHue: z.number(),
      avatarFileId: z.uuid().nullable(),
    })
    .nullable(),
  roles: z.array(roleKeySchema),
  derived: z.object({
    isLineManager: z.boolean(),
    headOfDepartmentIds: z.array(z.uuid()),
  }),
  extraPermissions: z.array(z.string()),
  platforms: z.object({ people: z.boolean(), admin: z.boolean(), ai: z.boolean() }),
  authMode: authModeSchema,
  persona: z.string().optional(),
  /** Globale Rechte ohne Ressourcenbezug (Aktionen aus can()), nur für UI-Hinweise. */
  permissions: z.array(z.string()),
  /** Heutiges Datum der Server-Uhr (APP_TODAY), YYYY-MM-DD. */
  today: z.string(),
});
export type Me = z.infer<typeof meSchema>;

export const mePatchSchema = z
  .object({
    displayName: z.string().trim().min(1).max(200),
    theme: themeSchema,
    notificationPrefs: z.record(z.string(), z.object({ inApp: z.boolean(), email: z.boolean() })),
  })
  .partial();

export const personaSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  employeeId: z.uuid(),
  displayName: z.string(),
  jobTitle: z.string(),
  avatarHue: z.number(),
  roles: z.array(roleKeySchema),
});
export type Persona = z.infer<typeof personaSchema>;

// ── Sitzungen, Kontowechsel, Anmelde-Historie ───────────────────────────────
export const sessionSchema = z.object({
  id: z.uuid(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof sessionSchema>;

export const browserAccountSchema = z.object({
  accountId: z.uuid(),
  /** Verknüpfte Person (Avatar-Farbe wie im übrigen Produkt); null bei Konten ohne Person. */
  employeeId: z.uuid().nullable(),
  displayName: z.string(),
  email: z.string(),
  avatarHue: z.number().nullable(),
  active: z.boolean(),
});
export type BrowserAccount = z.infer<typeof browserAccountSchema>;

export const loginEventSchema = z.object({
  id: z.uuid(),
  at: z.string(),
  success: z.boolean(),
  reason: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type LoginEvent = z.infer<typeof loginEventSchema>;

// ── Persönliche Tokens und Gerätefluss (R-38) ───────────────────────────────
export const tokenScopeSchema = z.enum(['read', 'write']);
export type TokenScope = z.infer<typeof tokenScopeSchema>;
export const tokenClientSchema = z.enum(['manual', 'connect', 'extension', 'macapp']);
export const deviceClientSchema = z.enum(['connect', 'extension', 'macapp']);

export const tokenCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  scope: tokenScopeSchema,
  expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(365)]).default(90),
});

export const apiTokenSchema = z.object({
  id: z.uuid(),
  accountId: z.uuid(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  client: tokenClientSchema,
  deviceName: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiToken = z.infer<typeof apiTokenSchema>;
/** Antwort auf POST /api/core/tokens: der Klartext erscheint nur hier, genau einmal. */
export const apiTokenCreatedSchema = z.object({ token: apiTokenSchema, secret: z.string() });

export const deviceCodeCreateSchema = z.object({
  client: deviceClientSchema,
  deviceName: z.string().trim().min(1).max(100),
  scopes: z.array(tokenScopeSchema).min(1).max(2),
});
export const deviceCodeCreatedSchema = z.object({
  deviceCode: z.string(),
  userCode: z.string(),
  verificationUri: z.string(),
  interval: z.number(),
  expiresIn: z.number(),
});
export const deviceTokenRequestSchema = z.object({ deviceCode: z.string().min(10).max(200) });
export const deviceCodeInfoSchema = z.object({
  userCode: z.string(),
  client: deviceClientSchema,
  deviceName: z.string(),
  scopes: z.array(tokenScopeSchema),
  status: z.enum(['pending', 'approved', 'denied', 'expired', 'used']),
  expiresAt: z.string(),
});
export const deviceDecisionSchema = z.object({ decision: z.enum(['approve', 'deny']) });
/** Antwort auf POST /api/core/device/token (immer 200; der Client fragt alle `interval` Sekunden). */
export const deviceTokenResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.enum(['authorization_pending', 'slow_down', 'access_denied', 'expired_token']),
  }),
  z.object({
    status: z.literal('approved'),
    token: z.string(),
    tokenId: z.uuid(),
    scopes: z.array(tokenScopeSchema),
    expiresAt: z.string().nullable(),
  }),
]);
export type DeviceTokenResponse = z.infer<typeof deviceTokenResponseSchema>;

// ── Aufgaben und Benachrichtigungen (R-34) ──────────────────────────────────
export const TASK_CATEGORIES = ['performance', 'career', 'rewards', 'kpis', 'tech', 'hr'] as const;
export const taskCategorySchema = z.enum(TASK_CATEGORIES);
export type TaskCategory = z.infer<typeof taskCategorySchema>;

export const taskSchema = z.object({
  id: z.uuid(),
  category: taskCategorySchema,
  module: z.string(),
  kind: z.string(),
  title: z.string(),
  subjectEmployeeId: z.uuid().nullable(),
  dueOn: z.string().nullable(),
  actionLabel: z.string().nullable(),
  actionUrl: z.string().nullable(),
  platform: platformSchema,
  status: z.enum(['open', 'done', 'cancelled']),
  snoozedUntil: z.string().nullable(),
  confidential: z.boolean(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});
export type Task = z.infer<typeof taskSchema>;

export const taskListQuerySchema = pageQuerySchema.extend({
  status: z.enum(['open', 'done', 'all']).default('open'),
  category: taskCategorySchema.optional(),
  includeSnoozed: z.stringbool().default(false),
});
export const taskCountsSchema = z.object({
  open: z.number(),
  overdue: z.number(),
  dueSoon: z.number(),
  byCategory: z.record(taskCategorySchema, z.number()),
});
export type TaskCounts = z.infer<typeof taskCountsSchema>;
export const taskSnoozeSchema = z.object({
  days: z.union([z.literal(1), z.literal(3), z.literal(7)]),
});

export const notificationSchema = z.object({
  id: z.uuid(),
  kind: z.string(),
  module: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  url: z.string().nullable(),
  confidential: z.boolean(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Notification = z.infer<typeof notificationSchema>;
export const notificationListQuerySchema = pageQuerySchema.extend({
  unread: z.stringbool().default(false),
});

// ── Ereignisse, Suche, Einstellungen, Dateien ───────────────────────────────
export const eventEnvelopeSchema = z.object({
  id: z.number(),
  type: z.string(),
  at: z.string(),
  data: z.unknown(),
});
export type EventEnvelope<T = unknown> = { id: number; type: string; at: string; data: T };

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  limit: z.coerce.number().int().min(1).max(20).default(8),
});
export const searchItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  href: z.string(),
  kind: z.string(),
});
export const searchGroupSchema = z.object({
  provider: z.string(),
  group: z.string(),
  items: z.array(searchItemSchema),
});
export const searchResponseSchema = z.object({ groups: z.array(searchGroupSchema) });
export type SearchItem = z.infer<typeof searchItemSchema>;
export type SearchGroup = z.infer<typeof searchGroupSchema>;

export const SETTING_PREFIXES = [
  'config.',
  'company.',
  'assistant.',
  'knowledge.',
  'chat.',
] as const;
export const settingKeySchema = z
  .string()
  .max(200)
  .regex(/^[a-z]+\.[A-Za-z0-9_.-]+$/)
  .refine((key) => SETTING_PREFIXES.some((p) => key.startsWith(p)), 'Unknown settings prefix');
export const settingSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type Setting = z.infer<typeof settingSchema>;
export const settingPutSchema = z.object({ value: z.json() });

export const signedUrlSchema = z.object({ url: z.string(), expiresAt: z.string() });
